'use client'
import { createContext, useState, useContext, useEffect } from 'react';
import { PatientDTO } from '@/dto/patient.dto';
import { ApiRoutes } from '../routes';
import { resolveRoute } from '../helpers';
import { useNextAuthAuthHeader } from '../hooks/useNextAuthAuthHeader';

interface PatientsContextType {
    isLoading: boolean;
    patients: PatientDTO[];
    isNextPage: boolean;
    isNextPageLoading: boolean;
    loadNextPage: () => void;
    searchByName: (name: string) => void;
}

const PatientsContext = createContext<PatientsContextType>({
    isLoading: true,
    patients: [],
    isNextPage: false,
    isNextPageLoading: false,
    loadNextPage: () => {},
    searchByName: () => {},
});

let nextPageMarker = '';

export function PatientsProvider({ children }: { children: React.ReactNode }) {
    const [patients, setPatients] = useState<PatientDTO[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isNextPage, setIsNextPage] = useState(false);
    const [isNextPageLoading, setIsNextPageLoading] = useState(false);
    const [nameParam, setNameParam] = useState('');

    const headers = useNextAuthAuthHeader()
    const authHeaders = headers['Authorization']
    useEffect(() => {
        if (authHeaders) {
          fetchPatients();
        }
    }, [authHeaders]);

    const fetchPatients = async (url?: string, reset = true) => {
        if (reset) setIsLoading(true);

        const fetchUrl = url || resolveRoute(ApiRoutes.PatientsAll);

        try {
            const res = await fetch(fetchUrl, {
                headers,
            });
            const { data, nextPage }: { data: PatientDTO[]; nextPage: string } = await res.json();

            nextPageMarker = nextPage || '';
            setIsNextPage(!!nextPage);

            setPatients((prev) => (reset ? data : [...prev, ...data]));
        } catch (err) {
            console.error('Fetching patients:', err);
        } finally {
            if (reset) setIsLoading(false);
        }
    };

    const loadNextPage = () => {
        if (!isNextPage || isNextPageLoading) return;

        setIsNextPageLoading(true);

        const query = new URLSearchParams();
        if (nextPageMarker) query.set('page', nextPageMarker);
        if (nameParam) query.set('name', nameParam);

        const url = `${resolveRoute(ApiRoutes.PatientsAll)}?${query.toString()}`;

        fetchPatients(url, false).finally(() => setIsNextPageLoading(false));
    };

    const searchByName = (name: string) => {
        if (name === nameParam) return;

        setNameParam(name);
        setPatients([]);
        nextPageMarker = '';

        const url = `${resolveRoute(ApiRoutes.PatientsAll)}?name=${encodeURIComponent(name)}`;
        fetchPatients(url, true);
    };

    return (
        <PatientsContext.Provider
            value={{
                isLoading,
                patients,
                isNextPage,
                isNextPageLoading,
                loadNextPage,
                searchByName,
            }}
        >
            {children}
        </PatientsContext.Provider>
    );
}

export function usePatients() {
    return useContext(PatientsContext);
}
